import { type } from 'arktype';
import type { ShiftSkillRequirement } from './shift-rules.model';

/**
 * Shift template - the reusable definition (name, capacity, skills).
 * Used by both backend (API responses) and frontend (grid rendering).
 */
export interface ShiftTemplate {
  id: string;
  name: string;
  capacity: number;
  requiredSkillIds: string[];
  /** Parsed skill requirements incl. minRating — source for qualification checks. */
  requiredSkillEntries?: ShiftSkillRequirement[];
  allowOverlap?: boolean;
  icon?: string | null;
  color?: string | null;
  /** ISO weekdays the shift is allowed on (0=Mon … 6=Sun). Empty/missing = unrestricted. */
  validWeekdays?: number[];
}

/**
 * ShiftUnit - time slot with embedded shift template.
 * This is THE canonical type for shift units across the entire application.
 *
 * A ShiftUnit represents a specific time slot for a shift. Multiple ShiftUnits
 * can belong to the same Shift (template). For example:
 * - "Kitchen" shift may have:
 *   - Morning ShiftUnit (06:00-14:00)
 *   - Evening ShiftUnit (14:00-22:00)
 */
export interface ShiftUnit {
  id: string;
  /** ISO 8601 datetime */
  startTime: string;
  /** ISO 8601 datetime */
  endTime: string;
  /** Default location for this unit, if configured. */
  locationId?: string | null;
  /** Whether assigned employees may override their own time/location for this unit. */
  employeeOverridable?: boolean;
  /** ISO 8601 datetime when soft-deleted, otherwise null */
  deletedAt?: string | null;
  /** Unit-level balance type override. `null` = no balance effect. `undefined` = use shift default. */
  balanceType?: string | null;
  /** Embedded shift template data */
  shift: ShiftTemplate;
}

/**
 * Minimal ShiftUnit info for assignment resolution.
 * Used by AssignmentResolver for legacy ID mapping.
 * Re-exported from assignment.utils for convenience.
 */
export interface ShiftUnitInfo {
  id: string;
  shiftId: string;
  /** ISO datetime - needed for deterministic resolution */
  startTime: string;
}

/**
 * Query parameters for ShiftUnit API endpoint.
 */
export interface ShiftUnitQuery {
  /** Filter by strategy ID */
  strategyId?: string;
  /** Filter by parent shift ID */
  shiftId?: string;
  /** Optional start date filter (YYYY-MM-DD) */
  startDate?: string;
  /** Optional end date filter (YYYY-MM-DD) */
  endDate?: string;
}

/**
 * ArkType schema for query validation
 */
export const shiftUnitQuerySchema = type({
  'strategyId?': 'string',
  'shiftId?': 'string',
  'startDate?': 'string',
  'endDate?': 'string',
});

export type ShiftUnitQueryValidated = typeof shiftUnitQuerySchema.t;

export const createShiftUnitSchema = type({
  shiftId: type('string').configure({
    description: 'ID of the parent shift this unit belongs to.',
    examples: ['shift_abc123'],
  }),
  startTime: type('string').configure({
    description: 'Start time of the shift unit as an ISO 8601 datetime.',
    examples: ['2026-04-22T06:00:00.000Z'],
  }),
  endTime: type('string').configure({
    description: 'End time of the shift unit as an ISO 8601 datetime.',
    examples: ['2026-04-22T14:00:00.000Z'],
  }),
  'locationId?': type('string | null').configure({
    description: 'Optional default location ID for this shift unit.',
    examples: ['location_abc123'],
  }),
  'employeeOverridable?': type('boolean').configure({
    description: 'When true, assigned employees may override their own time/location for this unit.',
    examples: [false],
  }),
}).configure({ description: 'Payload for creating a concrete time-slot (ShiftUnit) for a shift template.' });

export type CreateShiftUnit = typeof createShiftUnitSchema.t;

export const updateShiftUnitSchema = type({
  'employeeOverridable?': type('boolean').configure({
    description: 'When true, assigned employees may override their own time/location for this unit.',
    examples: [true],
  }),
  'locationId?': type('string | null').configure({
    description: 'Default location ID for this shift unit, or null to clear.',
    examples: ['location_abc123'],
  }),
  'balanceType?': type('string | null').configure({
    description:
      'Balance type key override for this shift unit. Overrides the shift-level default. `null` clears the override (falls back to shift default).',
    examples: ['flexitime', null],
  }),
}).configure({ description: 'Partial update payload for a shift unit.' });

export type UpdateShiftUnit = typeof updateShiftUnitSchema.t;
